import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Plus } from 'lucide-react';

import type { CreateIssueBody, IssueType, Priority } from '@tasks/contracts';

import { createIssue } from '../../api/issues';
import { queryKeys } from '../../api/queryKeys';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { Select, type SelectOption } from '../../components/Select';
import { Textarea } from '../../components/Textarea';
import { useToast } from '../../components/Toast';
import { useProjectMembers } from '../../hooks/useProject';
import { humanizeError } from '../../lib/errors';
import { useFormState } from '../../lib/useFormState';
import { ISSUE_TYPE_ORDER, PRIORITY_ORDER, issueTypeLabels, priorityLabels } from '../../i18n/enums';
import styles from './SprintFormModal.module.css';

const TYPE_OPTIONS: readonly SelectOption<IssueType>[] = ISSUE_TYPE_ORDER.filter((type) => type !== 'subtask').map(
  (value) => ({ value, label: issueTypeLabels[value] }),
);

const PRIORITY_OPTIONS: readonly SelectOption<Priority>[] = PRIORITY_ORDER.map((value) => ({
  value,
  label: priorityLabels[value],
}));

type FormValues = {
  readonly summary: string;
  readonly description: string;
  readonly type: IssueType;
  readonly priority: Priority;
  readonly assigneeId: string | null;
};

const INITIAL: FormValues = {
  summary: '',
  description: '',
  type: 'task',
  priority: 'medium',
  assigneeId: null,
};

/** Создание задачи сразу в конкретном спринте (кнопка «+ Создать» в блоке). */
export function CreateSprintIssueModal({
  open,
  sprintId,
  onClose,
}: {
  readonly open: boolean;
  readonly sprintId: string | null;
  readonly onClose: () => void;
}) {
  const { key = '' } = useParams<{ key: string }>();
  const members = useProjectMembers(key);
  const queryClient = useQueryClient();
  const toast = useToast();
  const form = useFormState<FormValues>(INITIAL, (values) =>
    values.summary.trim().length === 0 ? { summary: 'Введите заголовок' } : {},
  );

  const mutation = useMutation({
    mutationFn: (body: CreateIssueBody) => createIssue(key, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.backlog.view(key) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.board.root(key) });
      form.reset();
      onClose();
    },
    onError: (cause: unknown) => {
      toast.error(humanizeError(cause).message);
    },
  });

  const memberOptions: readonly SelectOption<string>[] = members.map((member) => ({
    value: member.user.id,
    label: member.user.displayName,
  }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Создать задачу в спринте"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Отмена
          </Button>
          <Button
            variant="primary"
            icon={<Plus size={14} />}
            loading={mutation.isPending}
            onClick={() => {
              if (!form.validate()) {
                return;
              }
              mutation.mutate({
                type: form.values.type,
                summary: form.values.summary.trim(),
                description: form.values.description,
                priority: form.values.priority,
                assigneeId: form.values.assigneeId,
                parentId: null,
                epicId: null,
                storyPoints: null,
                dueDate: null,
                sprintId,
                labels: [],
                components: [],
              });
            }}
          >
            Создать
          </Button>
        </>
      }
    >
      <div className={styles['form']}>
        <Input
          label="Заголовок"
          value={form.values.summary}
          error={form.errors.summary}
          onChange={(event) => form.setValue('summary', event.target.value)}
          onBlur={() => form.handleBlur('summary')}
        />
        <Textarea
          label="Описание"
          rows={3}
          value={form.values.description}
          onChange={(event) => form.setValue('description', event.target.value)}
        />
        <div className={styles['row']}>
          <Select
            label="Тип"
            options={TYPE_OPTIONS}
            value={form.values.type}
            onChange={(value) => form.setValue('type', value)}
          />
          <Select
            label="Приоритет"
            options={PRIORITY_OPTIONS}
            value={form.values.priority}
            onChange={(value) => form.setValue('priority', value)}
          />
        </div>
        <Select
          label="Исполнитель"
          options={memberOptions}
          value={form.values.assigneeId}
          placeholder="Не назначен"
          clearable
          onClear={() => form.setValue('assigneeId', null)}
          onChange={(value) => form.setValue('assigneeId', value)}
        />
      </div>
    </Modal>
  );
}

export function useLocalSprintId(): [string | null, (id: string | null) => void] {
  return useState<string | null>(null);
}
